import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface DrPlanView {
  readonly drPlanId: string;
  readonly drPlanCode: string;
  readonly drPlanName: string;
  readonly recoveryType: string;
  readonly scope: string | null;
  readonly recoverySteps: Record<string, unknown> | unknown[] | null;
  readonly rtoMinutes: number | null;
  readonly rpoMinutes: number | null;
  readonly status: string;
  readonly versionNo: number;
  readonly lastTestedAt: string | null;
  readonly nextTestDue: string | null;
}

/**
 * Normalise the persisted recovery runbook (jsonb) into an ordered step list.
 * DR plans store their runbook as `{ steps: [...] }` or a bare array (see §M
 * L31.02 runbook.step → dr_runbook_steps.step_text); each entry may be a plain
 * string or an object carrying its own text field.
 */
function parseSteps(raw: Record<string, unknown> | unknown[] | null): string[] {
  if (!raw) return [];
  const source = Array.isArray(raw) ? raw : (raw as Record<string, unknown>).steps;
  if (!Array.isArray(source)) return [];
  return source.map((s) => {
    if (typeof s === 'string') return s;
    if (s && typeof s === 'object') {
      const o = s as Record<string, unknown>;
      const v = o.text ?? o.step ?? o.instruction ?? o.description;
      if (typeof v === 'string') return v;
    }
    return String(s);
  });
}

/**
 * GR_M31-M35 §L31.03 / §Q9 — DR plan (runbook / drill) detail viewer
 * (`/admin/bcm/dr-plans/:id`). Renders a single disaster-recovery plan (T408)
 * with its recovery objectives (RTO / RPO) and the ordered recovery runbook
 * steps (§M L31.02 runbook.step) that an operator follows during a failover
 * drill. Read-only; DR authoring stays on the DR dashboard.
 */
export function DrPlanDetailPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [plan, setPlan] = useState<DrPlanView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const steps = useMemo(() => (plan ? parseSteps(plan.recoverySteps) : []), [plan]);

  useEffect(() => {
    let alive = true;
    if (!id) { setError(t('m31.drPlan.errNoId')); setLoading(false); return; }
    setLoading(true);
    apiRequest<{ data: DrPlanView }>(`/v1/bcm/dr-plans/${id}`, { token })
      .then((r) => { if (alive) { setPlan(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : t('m31.drPlan.errLoad')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id]);

  if (loading) return <main aria-label="dr-plan-detail"><p>{t('m31.drPlan.loading')}</p></main>;
  if (error) return <main aria-label="dr-plan-detail"><p role="alert">{error}</p></main>;
  if (!plan) return <main aria-label="dr-plan-detail"><p role="alert">{t('m31.drPlan.notFound')}</p></main>;

  return (
    <main aria-label="dr-plan-detail">
      <h1>{plan.drPlanName}</h1>
      <dl>
        <dt>{t('m31.drPlan.code')}</dt><dd aria-label="dr-code">{plan.drPlanCode}</dd>
        <dt>{t('m31.drPlan.recoveryType')}</dt><dd aria-label="dr-recovery-type">{plan.recoveryType}</dd>
        <dt>{t('m31.drPlan.rto')}</dt><dd aria-label="dr-rto">{plan.rtoMinutes != null ? t('m31.drPlan.minutes', { minutes: plan.rtoMinutes }) : '—'}</dd>
        <dt>{t('m31.drPlan.rpo')}</dt><dd aria-label="dr-rpo">{plan.rpoMinutes != null ? t('m31.drPlan.minutes', { minutes: plan.rpoMinutes }) : '—'}</dd>
        <dt>{t('m31.drPlan.status')}</dt><dd aria-label="dr-status">{plan.status}</dd>
        <dt>{t('m31.drPlan.version')}</dt><dd aria-label="dr-version">{plan.versionNo}</dd>
        <dt>{t('m31.drPlan.lastTested')}</dt><dd aria-label="dr-last-tested">{at(plan.lastTestedAt)}</dd>
      </dl>

      {plan.scope && <p aria-label="dr-scope">{plan.scope}</p>}

      <section aria-label="dr-runbook">
        <h2>{t('m31.drPlan.runbookHeading')}</h2>
        {steps.length > 0 ? (
          <ol aria-label="dr-step-list">
            {steps.map((step, i) => (
              <li key={`step-${i}`} aria-label={`dr-step-${i}`}>{step}</li>
            ))}
          </ol>
        ) : (
          <p aria-label="dr-no-steps">{t('m31.drPlan.noSteps')}</p>
        )}
      </section>
    </main>
  );
}
